import React, {useEffect, useRef, useState} from 'react';
import {ServiceResource, useAuth} from '../hooks/use-auth';
import {Notification} from '../navigation/page-layout';
import {CommonBridgeDataRequest, useInboundBridgeFilterData} from '../hooks/use-inbound-bridge-filter-data';
import {translateErrorToReactNode} from '../common';
import {Box, Container, ContentLayout, Grid, Header, KeyValuePairs, SpaceBetween, Spinner} from '@amzn/awsui-components-react';
import {BridgeType, InboundBridgeBucketRecord} from './index';
import {InboundBridgeFilters} from './inbound-bridge-filters';
import {useRetrieveInboundBridgeBucketLevelAgg} from '../hooks/use-analytics-api';
import '../../assets/styles/inbound-bridging-styles.scss';
import {InboundBridgePieChart} from './inbound-bridge-pie-chart';

const COMPONENT_TEXTS = {
    page_description: 'Plan to actuals bridging',
} as const;

export function InboundBridgeP2ANetwork(props: {pushNotification: (notification: Notification) => void}) {
    function createErrorListener(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const auth = useAuth();
    const [planActuals, setPlanActuals] = useState<{plan: number; actuals: number}>({plan: 0, actuals: 0});
    const [rescaledWapeContribs, setRescaledWapeContribs] = useState<InboundBridgeBucketRecord[]>([]);
    const isLoadingPlanActuals = useRef<boolean>(true);
    const isLoadingRescaledWapeContribs = useRef<boolean>(true);

    const tenant = {
        business: auth.authInformation!.current!.businessId,
        country: auth.authInformation!.current!.country,
    };

    const clientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView);

    const {
        execute: executeRetrievePlanActuals,
        status: retrievePlanActualsStatus,
        value: retrievePlanActualsResponse,
    } = useRetrieveInboundBridgeBucketLevelAgg(clientConfiguration, createErrorListener('RetrievePlanActuals failed'), [auth]);

    const {
        execute: executeRetrieveRescaledWapeContribs,
        status: retrieveRescaledWapeContribsStatus,
        value: retrieveRescaledWapeContribsResponse,
    } = useRetrieveInboundBridgeBucketLevelAgg(clientConfiguration, createErrorListener('RetrieveRescaledWapeContribs failed'), [
        auth,
    ]);

    const {nodeFilterDataMap, selectedFilter, setSelectedFilter, isLoadingFilterData} = useInboundBridgeFilterData(
        BridgeType.P2A,
        tenant,
        true,
        clientConfiguration,
        createErrorListener,
        (params: CommonBridgeDataRequest) => {
            executeRetrievePlanActuals({
                ...tenant,
                forecastType: 'sop',
                bridgeType: BridgeType.P2A,
                filter: JSON.stringify({
                    nodes: ['ALL'],
                    balanceDateStart: params.balanceDateStart,
                    balanceDateEnd: params.balanceDateEnd,
                    fromHorizonInWeeks: params.fromHorizonInWeeks,
                    toHorizonInWeeks: params.toHorizonInWeeks,
                }),
                timeGranularity: 'ALL',
                metrics: JSON.stringify(['value']),
            });

            executeRetrieveRescaledWapeContribs({
                ...tenant,
                forecastType: 'sop',
                bridgeType: BridgeType.P2A,
                filter: JSON.stringify({
                    nodes: ['ALL'],
                    balanceDateStart: params.balanceDateStart,
                    balanceDateEnd: params.balanceDateEnd,
                    fromHorizonInWeeks: params.fromHorizonInWeeks,
                    toHorizonInWeeks: params.toHorizonInWeeks,
                }),
                timeGranularity: 'ALL',
                metrics: JSON.stringify(['wapeContribRescaled']),
            });
        }
    );

    isLoadingPlanActuals.current = ['idle', 'pending'].includes(retrievePlanActualsStatus);
    isLoadingRescaledWapeContribs.current = ['idle', 'pending'].includes(retrieveRescaledWapeContribsStatus);

    useEffect(() => {
        if (
            !isLoadingPlanActuals.current &&
            retrievePlanActualsResponse?.records &&
            retrievePlanActualsResponse.records.length > 1
        ) {
            const newData = retrievePlanActualsResponse.records.sort((a, b) => a.seqId - b.seqId);
            setPlanActuals({
                plan: newData[0].value ?? 0,
                actuals: newData[newData.length - 1].value ?? 0,
            });
        }
    }, [isLoadingPlanActuals.current]);

    useEffect(() => {
        if (
            !isLoadingRescaledWapeContribs.current &&
            retrieveRescaledWapeContribsResponse?.records &&
            retrieveRescaledWapeContribsResponse.records.length > 1
        ) {
            setRescaledWapeContribs(
                retrieveRescaledWapeContribsResponse.records.map(
                    (record) =>
                        ({
                            date: '1900-01-01',
                            seqId: record.seqId,
                            name: record.name,
                            wapeContribRescaled: record.wapeContribRescaled,
                        } as InboundBridgeBucketRecord)
                )
            );
        }
    }, [isLoadingRescaledWapeContribs.current]);

    return (
        <ContentLayout defaultPadding>
            <SpaceBetween direction="vertical" size="xxl">
                <Header variant="h2" description={COMPONENT_TEXTS.page_description}>
                    Network-Level Overview
                </Header>
                <InboundBridgeFilters
                    bridgeType={BridgeType.P2A}
                    tenant={tenant}
                    nodeSelectConfig={{hideNodeSelect: true}}
                    nodeFilterDataMap={nodeFilterDataMap}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    isLoadingFilterData={isLoadingFilterData}
                    isLoadingData={isLoadingPlanActuals.current || isLoadingRescaledWapeContribs.current}
                    onApply={() => {
                        if (selectedFilter) {
                            isLoadingPlanActuals.current = true;
                            executeRetrievePlanActuals({
                                ...tenant,
                                forecastType: 'sop',
                                bridgeType: BridgeType.P2A,
                                filter: JSON.stringify({
                                    nodes: ['ALL'],
                                    balanceDateStart: selectedFilter.balanceDateStart,
                                    balanceDateEnd: selectedFilter.balanceDateEnd,
                                    fromHorizonInWeeks: selectedFilter.fromHorizonInWeeks,
                                    toHorizonInWeeks: selectedFilter.toHorizonInWeeks,
                                    channels: selectedFilter.channels,
                                    ibTypes: selectedFilter.ibTypes,
                                    tempZones: selectedFilter.tempZones,
                                }),
                                timeGranularity: 'ALL',
                                metrics: JSON.stringify(['value']),
                            });

                            isLoadingRescaledWapeContribs.current = true;
                            executeRetrieveRescaledWapeContribs({
                                ...tenant,
                                forecastType: 'sop',
                                bridgeType: BridgeType.P2A,
                                filter: JSON.stringify({
                                    nodes: ['ALL'],
                                    balanceDateStart: selectedFilter.balanceDateStart,
                                    balanceDateEnd: selectedFilter.balanceDateEnd,
                                    fromHorizonInWeeks: selectedFilter.fromHorizonInWeeks,
                                    toHorizonInWeeks: selectedFilter.toHorizonInWeeks,
                                    channels: selectedFilter.channels,
                                    ibTypes: selectedFilter.ibTypes,
                                    tempZones: selectedFilter.tempZones,
                                }),
                                timeGranularity: 'ALL',
                                metrics: JSON.stringify(['wapeContribRescaled']),
                            });
                        }
                    }}
                />
                {renderVisualizations()}
            </SpaceBetween>
        </ContentLayout>
    );

    function renderVisualizations(): React.ReactElement {
        if (isLoadingFilterData) {
            return (
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            );
        }

        return (
            <Grid gridDefinition={[{colspan: 5}, {colspan: 7}]}>
                {renderOverview()}
                <InboundBridgePieChart data={rescaledWapeContribs} isLoading={isLoadingRescaledWapeContribs.current} />
            </Grid>
        );
    }

    function renderOverview(): React.ReactElement {
        if (isLoadingPlanActuals.current) {
            return (
                <Container header={<Header variant="h2">Aggregated Overview</Header>}>
                    <Box textAlign="center" color="inherit">
                        <Spinner size="large" />
                    </Box>
                </Container>
            );
        }

        return (
            <Container
                header={
                    <Header variant="h2" description="Overview of planned versus actual units.">
                        Aggregated Overview
                    </Header>
                }
            >
                <KeyValuePairs
                    columns={2}
                    items={[
                        {
                            label: 'Plan',
                            value: (
                                <Box color="text-status-success" variant="awsui-value-large">
                                    {planActuals.plan.toLocaleString()}
                                </Box>
                            ),
                        },
                        {
                            label: 'Actuals',
                            value: (
                                <Box color="text-status-info" variant="awsui-value-large">
                                    {planActuals.actuals.toLocaleString()}
                                </Box>
                            ),
                        },
                    ]}
                />
            </Container>
        );
    }
}
